import React, { Fragment, useState, useEffect, useRef } from "react";
import ZPopover from "@atoms/ZPopover";
import ZTypography from "@atoms/ZTypography";
import ZInput, { InputWidths } from "@molecules/ZInput";
import ZButton from "@atoms/ZButton";
import ZCheckbox from "@atoms/ZCheckbox";
import ZIcon from "@atoms/ZIcon";
import ZTooltip from "@atoms/ZTooltip";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { Row, Tag, Divider, List } from "antd";
import { IFilterMenuUIProps } from "./interface";
import "./index.less";

const ZFilterMenu: React.FC<IFilterMenuUIProps> = ({
	isVisible = false,
	currentActiveFilter,
	fieldKeyword = "",
	filtersMap = [],
	showOperatorList = false,
	operatorKeyword = "",
	filteredAllowedOperators = [],
	showValueField = false,
	shouldDisableSubmitButton = true,
	showArchivedCheckbox = false,
	...rest
}) => {
	const [hasCheckboxChanged, setHasCheckboxChanged] = useState(false);
	const [localShowArchivedRecords, setLocalShowArchivedRecords] = useState(
		rest.showArchivedRecords ?? false
	);
	const openPopoverValueRef = useRef(rest.showArchivedRecords ?? false);

	useEffect(() => {
		if (isVisible) {
			setLocalShowArchivedRecords(rest.showArchivedRecords ?? false);
			openPopoverValueRef.current = rest.showArchivedRecords ?? false;
		} else {
			setHasCheckboxChanged(false);
		}
	}, [isVisible, rest.showArchivedRecords]);

	const handleCheckboxChange = (checked: boolean) => {
		setLocalShowArchivedRecords(checked);
		setHasCheckboxChanged(checked !== openPopoverValueRef.current);
	};

	return (
		<ZPopover
			size="sm"
			placement="rightTop"
			hideArrow={true}
			trigger="click"
			visible={isVisible}
			onVisibleChange={rest.onVisibleChange}
			destroyTooltipOnHide
			removeDefaultPadding={true}
			content={
				<div className="filter-container py-4">
					<Row className="filter-search-flow-container flex flex-col">
						{(!currentActiveFilter ||
							!currentActiveFilter?.field_name) && (
							<Fragment>
								<ZTypography.ZText
									colorVariant={7}
									className="block mb-2 px-4"
								>
									FIELD SELECTED
								</ZTypography.ZText>
								<ZInput
									width={InputWidths.full}
									inputType="search"
									placeholder="Search"
									allowClear
									value={fieldKeyword}
									onChange={rest.handleFieldKeywordChange}
									className="px-4"
								/>
							</Fragment>
						)}
						{!!currentActiveFilter?.field_display_name && (
							<Fragment>
								<ZTypography.ZText
									colorVariant={7}
									className="block mb-2 px-4"
								>
									FIELD SELECTED
								</ZTypography.ZText>
								<Tag
									className="filter-tag mb-2 mx-4"
									closable={true}
									onClose={rest.removeSelectedFilterField}
								>
									{currentActiveFilter.field_display_name}
								</Tag>
								{!!currentActiveFilter.filter_operator
									?.display_value && (
									<Fragment>
										<Divider className="my-2" />
										<ZTypography.ZText
											colorVariant={7}
											className="block mb-2 px-4"
										>
											CONDITION
										</ZTypography.ZText>
										<Tag
											className="mb-2 mx-4 filter-tag"
											closable={true}
											onClose={
												rest.removeSelectedOperator
											}
										>
											{
												currentActiveFilter
													.filter_operator
													.display_value
											}
										</Tag>
									</Fragment>
								)}
							</Fragment>
						)}
						{!currentActiveFilter?.field_display_name && (
							<div className="scroll-container w-full mt-4">
								{filtersMap?.map(listData => (
									<div
										className="field-list-data-container"
										key={listData.entity}
									>
										<List
											dataSource={listData.filters}
											renderItem={filter => (
												<List.Item
													className="px-4 cursor-pointer hover:bg-geekBlue-1 border-0"
													onClick={() =>
														rest.handleFilterFieldSelect(
															filter
														)
													}
												>
													<ZTypography.ZText
														level={1}
														className="truncate"
													>
														{
															filter.field_display_name
														}
													</ZTypography.ZText>
												</List.Item>
											)}
										/>
									</div>
								))}
							</div>
						)}
						{showOperatorList && (
							<>
								<Divider className="my-2" />
								<ZTypography.ZText
									colorVariant={7}
									className="block mb-2 px-4"
								>
									CONDITION
								</ZTypography.ZText>
								<ZInput
									inputType="search"
									placeholder="Search"
									width={InputWidths.full}
									allowClear
									value={operatorKeyword}
									onChange={rest.handleOperatorKeywordChange}
									className="px-4"
								/>
								<div className="scroll-container mt-4">
									<div className="field-list-data-container">
										<List
											dataSource={
												filteredAllowedOperators
											}
											renderItem={operator => (
												<List.Item
													className="px-4 cursor-pointer hover:bg-geekBlue-1 border-0"
													onClick={() =>
														rest.handleOperatorSelect(
															operator
														)
													}
												>
													{operator.display_value}
												</List.Item>
											)}
										/>
									</div>
								</div>
							</>
						)}
						{showValueField && (
							<>
								<Divider className="my-2" />
								<ZTypography.ZText
									colorVariant={7}
									className="block mb-2 px-4"
								>
									VALUE
								</ZTypography.ZText>
								<div className="value-input w-full px-4">
									{rest.getFieldForValue()}
								</div>
							</>
						)}
					</Row>
					{showArchivedCheckbox && (
						<>
							<Divider className="my-2" />
							<Row className="px-4 mt-2 h-3">
								<ZCheckbox
									checked={localShowArchivedRecords}
									onChange={e =>
										handleCheckboxChange(e.target.checked)
									}
								>
									Show Archived Records
									<ZTooltip
										title="Displays records that have been archived or marked inactive in your PSA."
										placement="top"
										overlayClassName="font-normal text-sm leading-[22px]"
									>
										<ZIcon
											name={EIcons.WARNINGCIRCLE}
											size="sm"
											color={EColors.GRAY}
											colorVariant={7}
											className="ml-2 align-middle"
										/>
									</ZTooltip>
								</ZCheckbox>
							</Row>
						</>
					)}
					<Row className="flex items-center justify-end pt-4 px-4 mt-4 border-0 border-t border-solid border-gray-5">
						<ZButton
							className="mr-4"
							onClick={rest.onFilterPopoverBackButtonClick}
							size="small"
						>
							{!!currentActiveFilter?.field_display_name ||
							!!currentActiveFilter?.filter_operator
								? "Back"
								: "Cancel"}
						</ZButton>
						<ZButton
							type="primary"
							disabled={
								shouldDisableSubmitButton && !hasCheckboxChanged
							}
							onClick={() => {
								const hasValidFilter =
									currentActiveFilter &&
									!shouldDisableSubmitButton;

								if (hasValidFilter) {
									rest.handleFilterSubmit();
								}

								if (hasCheckboxChanged) {
									if (rest.onShowArchivedRecordsChange) {
										rest.onShowArchivedRecordsChange(
											localShowArchivedRecords
										);
									}
								}

								if (!hasValidFilter && hasCheckboxChanged) {
									rest.onVisibleChange(false);
								}
							}}
							data-testid="submit"
							size="small"
						>
							{!!rest.selectedFilter ? "Save" : "Apply"}
						</ZButton>{" "}
					</Row>
				</div>
			}
		>
			{rest.children}
		</ZPopover>
	);
};

export default ZFilterMenu;
